import { useEffect, useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/utils";
import { useVapiAssistantStats } from "@/hooks/use-vapi-assistant-stats";
import type { DashboardStatsQuery, VapiAssistantStats } from "@/types";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  statsQuery: DashboardStatsQuery;
  refetchKey: number;
  onLoadingChange?: (loading: boolean) => void;
  vapiFromBundle?: VapiAssistantStats | null;
  bundleLoading?: boolean;
  onBundleRefresh?: () => void;
};

/** Aligned with path analytics / transition-retry chart bar palette. */
const CHART_BAR_FILLS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "#8b5cf6",
  "hsl(var(--chart-3))",
  "#3b82f6",
  "#a855f7",
];

function shortId(id: string): string {
  const t = id.trim();
  if (t.length <= 10) return t;
  return `${t.slice(0, 8)}…`;
}

export function VapiAssistantChart({
  statsQuery,
  refetchKey,
  onLoadingChange,
  vapiFromBundle,
  bundleLoading,
  onBundleRefresh,
}: Props) {
  const useBundle = vapiFromBundle !== undefined;
  const {
    data: fetched,
    loading: fetchLoading,
    error,
    refresh,
  } = useVapiAssistantStats(statsQuery, {
    refetchKey,
    enabled: !useBundle,
  });
  const data = useBundle ? vapiFromBundle : fetched;
  const loading = useBundle ? Boolean(bundleLoading) : fetchLoading;
  const doRefresh = useBundle ? onBundleRefresh ?? (() => {}) : refresh;

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const rows = useMemo(() => {
    const list = data?.assistants ?? [];
    return list.map((a) => ({
      ...a,
      label: shortId(a.assistant_id),
      successPercent: Math.round(a.success_rate * 1000) / 10,
    }));
  }, [data]);

  const isInitial = loading && !data;
  const isUpdating = loading && Boolean(data);

  return (
    <section
      className="rounded-xl border border-border bg-card shadow-sm"
      aria-label="Assistant success rate"
    >
      <div
        className="border-b border-violet-500/15 bg-gradient-to-br from-violet-500/[0.09] via-card to-cyan-500/[0.07] px-4 py-3 sm:px-5 sm:py-4"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-violet-600/90 dark:text-violet-300/90">
          Voice Assistant
        </p>
        <h2 className="mt-1.5 text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
          <span className="text-amber-600/95 dark:text-amber-400">Success rate</span>
          <span className="text-muted-foreground"> — </span>
          <span className="text-emerald-600/95 dark:text-emerald-400">by assistant</span>
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Each <span className="font-mono text-[11px]">vapiAssistantIds</span> index is
          matched to <span className="font-mono text-[11px]">structured_outputs_history</span> at
          the same index; success is per call attempt (history entry). Case-level
          dashboard success is not used.
        </p>
        {data?.capped ? (
          <p className="mt-2 text-xs text-amber-700/90 dark:text-amber-400/90">
            No case filters: using the {data.cap} most recent cases. Add filters
            to scope a different set.
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="px-4 py-2 text-sm text-destructive sm:px-5" role="alert">
          {error}
        </p>
      ) : null}

      <div className="space-y-3 px-4 pb-5 sm:px-5 sm:pb-6">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {[
            {
              label: "Documents scanned",
              value: data?.scanned_case_count,
              className:
                "bg-violet-500/10 text-violet-900 dark:text-violet-200 border-violet-500/20",
            },
            {
              label: "Cases w/ paired call data",
              value: data?.cases_with_assistant,
              className:
                "bg-slate-500/8 text-foreground border-border",
            },
            {
              label: "Paired call attempts",
              value: data?.call_attempts_total,
              className:
                "bg-cyan-500/10 text-cyan-900 dark:text-cyan-200 border-cyan-500/20",
            },
            {
              label: "Assistant IDs in chart",
              value: data?.assistants?.length ?? null,
              className:
                "bg-slate-500/8 text-foreground border-border",
            },
          ].map((row) => (
            <span
              key={row.label}
              className={cn(
                "inline-flex items-baseline gap-1.5 rounded-md border px-2.5 py-1",
                row.className,
              )}
            >
              <span className="text-xs text-muted-foreground">{row.label}</span>
              <span className="font-semibold tabular-nums text-foreground">
                {row.value ?? "—"}
              </span>
            </span>
          ))}
          {isUpdating ? (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              Updating…
            </span>
          ) : null}
          <button
            type="button"
            className="ml-auto text-xs font-medium text-violet-600 hover:underline dark:text-violet-400"
            onClick={() => void doRefresh()}
          >
            Refresh
          </button>
        </div>

        {isInitial ? (
          <div className="space-y-2 py-6" aria-hidden>
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-48 w-full rounded-lg" />
          </div>
        ) : rows.length === 0 && !loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center max-w-md mx-auto">
            No cases in this set have a last VAPI assistant id. Placed or retried
            cases will populate this chart.
          </p>
        ) : (
          <div
            className={cn(
              "relative w-full min-w-0 pl-0 pr-1 sm:pr-0",
              isUpdating && "opacity-60",
            )}
          >
            {isUpdating ? (
              <div
                className="pointer-events-none absolute inset-0 z-10 flex items-start justify-end pt-2 pr-2"
                aria-live="polite"
              >
                <span className="rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
                  Loading…
                </span>
              </div>
            ) : null}
            <div className="h-80 w-full min-h-[18rem] rounded-lg border border-violet-500/10 bg-gradient-to-b from-violet-500/[0.04] to-transparent px-1 pt-2 pb-3 sm:px-2 sm:pb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  layout="vertical"
                  margin={{ top: 8, right: 28, left: 4, bottom: 8 }}
                  barCategoryGap="14%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--chart-grid) / 0.45)"
                    horizontal
                    vertical={false}
                  />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    unit="%"
                    label={{
                      value: "Success rate",
                      position: "insideBottom",
                      offset: -4,
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    width={100}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const p = payload[0].payload as {
                        assistant_id: string;
                        successful: number;
                        total: number;
                        successPercent: number;
                      };
                      return (
                        <div
                          className={cn(
                            "rounded-lg border border-violet-500/20 bg-popover/95 px-3 py-2 text-sm shadow-lg backdrop-blur-sm",
                          )}
                        >
                          <p
                            className="font-mono text-[11px] text-foreground break-all"
                            title={p.assistant_id}
                          >
                            {p.assistant_id}
                          </p>
                          <p className="text-muted-foreground">
                            {p.successful} / {p.total} succeeded (
                            {p.successPercent}
                            %)
                          </p>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="successPercent"
                    name="Success rate"
                    radius={[0, 6, 6, 0]}
                  >
                    {rows.map((row, i) => (
                      <Cell
                        key={row.assistant_id}
                        fill={CHART_BAR_FILLS[i % CHART_BAR_FILLS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
